<template>
    <div class="bg-white min-h-full">
        <div :class="width" class="mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-12">
            <ui-header :title="title" :subtitle="subtitle"></ui-header>

            <slot name="filters" :filters="filters" v-if="! noFilters">
                <div class="flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-6 mt-8">
                    <ui-select v-if="hasDateRange" label="Date Range" class="md:w-64" v-model="filters.range" :options="dateRangeOptions" @input="setFromToFilters"></ui-select>
                    <ui-date-picker label="From" class="md:w-64" v-model="filters.from" @input="setCustomRange"></ui-date-picker>
                    <ui-date-picker label="To" class="md:w-64" v-model="filters.to" @input="setCustomRange"></ui-date-picker>
                    <slot name="additional-filters" :filters="filters"></slot>
                </div>
            </slot>

            <div class="grid mt-16" :class="grid">
                <slot :filters="filters"></slot>
            </div>

        </div>
    </div>
</template>

<script>
import moment from 'moment'

const getFiscalYearTimestamps = () => {
    if (moment().quarter() === 4) {
        return {
            current: {
                from: moment().month('October').startOf('month'),
                to: moment().add(1, 'year').month('September').endOf('month')
            },
            last: {
                from: moment().subtract(1, 'year').month('October').startOf('month'),
                to: moment().month('September').endOf('month')
            }
        };
    } else {
        return {
            current: {
                from: moment().subtract(1, 'year').month('October').startOf('month'),
                to: moment().month('September').endOf('month')
            },
            last: {
                from: moment().subtract(2, 'year').month('October').startOf('month'),
                to: moment().subtract(1, 'year').month('September').endOf('month')
            }
        };
    }
};

const dateRanges = [
    {
        value: 'today',
        label: 'Today',
        handler: () => ({
            from: moment(),
            to: moment(),
        })
    },
    {
        value: 'yesterday',
        label: 'Yesterday',
        handler: () => ({
            from: moment().subtract(1, 'day'),
            to: moment().subtract(1, 'day'),
        })
    },
    {
        value: 'since-monday',
        label: 'Since Monday',
        handler: () => ({
            from: moment().startOf('isoWeek'),
            to: moment(),
        })
    },
    {
        value: 'last-week',
        label: 'Last Week',
        handler: () => ({
            from: moment().subtract(1, 'week').startOf('isoWeek'),
            to: moment().subtract(1, 'week').endOf('isoWeek'),
        })
    },
    {
        value: 'this-month',
        label: 'This Month',
        handler: () => ({
            from: moment().startOf('month'),
            to: moment(),
        })
    },
    {
        value: 'last-month',
        label: 'Last Month',
        handler: () => ({
            from: moment().subtract(1, 'month').startOf('month'),
            to: moment().subtract(1, 'month').endOf('month'),
        })
    },
    {
        value: 'this-quarter',
        label: 'This Quarter',
        handler: () => ({
            from: moment().startOf('quarter'),
            to: moment(),
        })
    },
    {
        value: 'last-quarter',
        label: 'Last Quarter',
        handler: () => ({
            from: moment().subtract(1, 'quarter').startOf('quarter'),
            to: moment().subtract(1, 'quarter').endOf('quarter'),
        })
    },
    {
        value: 'this-year',
        label: 'This Year',
        handler: () => ({
            from: moment().startOf('year'),
            to: moment(),
        })
    },
    {
        value: 'last-year',
        label: 'Last Year',
        handler: () => ({
            from: moment().subtract(1, 'year').startOf('year'),
            to: moment().subtract(1, 'year').endOf('year'),
        })
    },
    {
        value: 'this-financial-year',
        label: 'This Financial Year',
        handler: () => ({
            from: getFiscalYearTimestamps().current.from,
            to: moment(),
        }),
    },
    {
        value: 'last-financial-year',
        label: 'Last Financial Year',
        handler: () => getFiscalYearTimestamps().last
    },
    {
        value: 'days-7',
        label: 'Last 7 days',
        handler: () => ({
            from: moment().subtract(8, 'days'),
            to: moment().subtract(1, 'day'),
        })
    },
    {
        value: 'days-14',
        label: 'Last 14 days',
        handler: () => ({
            from: moment().subtract(15, 'days'),
            to: moment().subtract(1, 'day'),
        })
    },
    {
        value: 'days-30',
        label: 'Last 30 days',
        handler: () => ({
            from: moment().subtract(31, 'days'),
            to: moment().subtract(1, 'day'),
        })
    },
    {
        value: 'days-90',
        label: 'Last 90 days',
        handler: () => ({
            from: moment().subtract(91, 'days'),
            to: moment().subtract(1, 'day'),
        })
    },
]

export default {
    props: {
        title: String,
        subtitle: String|Array,
        noFilters: Boolean,
        initialFilters: {
            type: Object,
            default: () => ({}),
        },
        width: {
          type: String,
          default: 'max-w-3xl lg:max-w-screen-xl'
        },
        grid: {
            type: String,
            default: 'gap-10 grid-cols-12'
        },
        includeRange: {
            type: Array,
            default: () => ([]),
        },
        excludeRange: {
            type: Array,
            default: () => ([]),
        },
    },
    data () {
        const dateRangeOptions = dateRanges
            .filter(({ value }) => this.includeRange.length === 0 || this.includeRange.includes(value))
            .filter(({ value }) => ! this.excludeRange.includes(value))

        dateRangeOptions.push({
            value: 'custom',
            label: 'Custom',
            handler: () => ({ from: null, to: null }),
        })

        const filters = { ...this.initialFilters }

        if (this.initialFilters.range) {
            const { from, to } = this.getFromToFilters(dateRangeOptions, this.initialFilters.range)
            filters.from = from?.format('YYYY-MM-DD') ?? null
            filters.to = to?.format('YYYY-MM-DD') ?? null
        }

        return {
            filters,
            dateRangeOptions,
        }
    },
    computed: {
        hasDateRange () {
            return !!this.filters.range
        }
    },
    methods: {
        setCustomRange () {
            if (!this.hasDateRange) return
            this.filters.range = 'custom'
        },
        getFromToFilters (options, rangeValue) {
            const range = options.find(({ value }) => value === rangeValue)
            if (!range) return { from: null, to: null }
            return range.handler()
        },
        setFromToFilters (rangeValue) {
            const newFilters = this.getFromToFilters(this.dateRangeOptions, rangeValue)
            this.filters.from = newFilters?.from?.format('YYYY-MM-DD') ?? this.filters.from
            this.filters.to = newFilters?.to?.format('YYYY-MM-DD') ?? this.filters.to
        },
    },
    mounted () {
        $('#app-content').css('padding',0);
    },
}
</script>
